import { linkedSignal, signal, Signal } from '@angular/core';

import { ExtendableDropdownOption } from '@shared/components/extendable-options-dropdown/extendable-options-dropdown.component';

import { BalanceSheetAccount } from '../components/balance-sheet-account-grid/grid-config/grid-config-context';

export interface BalanceSheetProgramProjectContext {
  onAddProgramOption: (option: ExtendableDropdownOption) => void;
  onAddProjectOption: (option: ExtendableDropdownOption) => void;
  programOptions: Signal<ExtendableDropdownOption[]>;
  projectOptions: Signal<ExtendableDropdownOption[]>;
}

export function useBalanceSheetProgramProjectContext({
  originalRowData,
}: {
  originalRowData: Signal<BalanceSheetAccount[]>;
}) {
  // these are the default options that are passed to the modal
  const defaultProgramOptions = signal<string[]>([]);
  const defaultProjectOptions = signal<string[]>([]);

  // Extract program options from existing row data
  const programOptions = linkedSignal(() => {
    const data = originalRowData();
    const defaultOptions = defaultProgramOptions();
    const uniquePrograms = new Set<string>();

    data.forEach((row) => {
      if (row.program && row.program.trim()) {
        uniquePrograms.add(row.program.trim());
      }
    });

    defaultOptions.forEach((option) => {
      uniquePrograms.add(option);
    });

    return Array.from(uniquePrograms)
      .sort()
      .map((value) => ({
        id: value,
        value,
      }));
  });

  // Extract project options from existing row data
  const projectOptions = linkedSignal(() => {
    const data = originalRowData();
    const defaultOptions = defaultProjectOptions();
    const uniqueProjects = new Set<string>();

    data.forEach((row) => {
      if (row.project && row.project.trim()) {
        uniqueProjects.add(row.project.trim());
      }
    });

    defaultOptions.forEach((option) => {
      uniqueProjects.add(option);
    });

    return Array.from(uniqueProjects)
      .sort()
      .map((value) => ({
        id: value,
        value,
      }));
  });

  // Add a new program option
  const onAddProgramOption = (option: ExtendableDropdownOption) => {
    const current = programOptions();

    // Avoid duplicates by value
    if (!current.some((o) => o.value === option.value)) {
      programOptions.set([...current, option].sort((a, b) => a.value.localeCompare(b.value)));
    }
  };

  // Add a new project option
  const onAddProjectOption = (option: ExtendableDropdownOption) => {
    const current = projectOptions();

    // Avoid duplicates by value
    if (!current.some((o) => o.value === option.value)) {
      projectOptions.set([...current, option].sort((a, b) => a.value.localeCompare(b.value)));
    }
  };

  return {
    defaultProgramOptions,
    defaultProjectOptions,
    onAddProgramOption,
    onAddProjectOption,
    programOptions: programOptions.asReadonly(),
    projectOptions: projectOptions.asReadonly(),
  };
}
